import { cn } from '@neo/lib/utils'
import { PlayPauseIcon } from '@neo/primitives/audio-player'
import { Typography } from '@neo/primitives/typography'
import { CheckIcon, Loader2 } from 'lucide-react'
import type { MouseEvent } from 'react'
import type { CommunityVoice } from 'v2/pages/flow/types'

export interface VoiceItemProps {
  voice: CommunityVoice
  isSelected: boolean
  onSelect: () => void
  isPlaying: boolean
  isLoading: boolean
  onPlayPreview: () => void
  onStop: () => void
}

export const VoiceItem = ({
  voice,
  isSelected,
  onSelect,
  isPlaying,
  isLoading,
  onPlayPreview,
  onStop,
}: VoiceItemProps) => {
  const handlePlayClick = (e: MouseEvent) => {
    e.stopPropagation()
    if (isPlaying) {
      onStop()
    } else {
      onPlayPreview()
    }
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: the selectable card contains a separate preview button
    <div
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-md border p-3 text-left transition-colors',
        isSelected
          ? 'border-brand bg-hv1'
          : 'border-border bg-background hover:border-brand/60 hover:bg-hv1',
      )}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect()
        }
      }}
      data-testid={`button-community-voice-item-${voice.voice_id}`}
    >
      <button
        type="button"
        className="shrink-0"
        onClick={handlePlayClick}
        data-testid={`button-play-preview-${voice.voice_id}`}
        title={isPlaying ? 'Pause preview' : 'Play preview'}
      >
        {isLoading ? (
          <Loader2 className="size-5 animate-spin text-brand-text" />
        ) : (
          <PlayPauseIcon className="size-7" playing={isPlaying} />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Typography variant="P/500" className="truncate text-foreground">
            {voice.name}
          </Typography>
          {isSelected && <CheckIcon className="size-4 shrink-0 text-brand-text" />}
        </div>
        <Typography variant="XS/400" className="truncate text-secondary">
          {voice.description}
        </Typography>
        <div className="flex gap-2 mt-1">
          <span className="text-xxs-400 text-tertiary capitalize">{voice.gender}</span>
          <span className="text-xxs-400 text-tertiary">•</span>
          <span className="text-xxs-400 text-tertiary capitalize">{voice.accent}</span>
          <span className="text-xxs-400 text-tertiary">•</span>
          <span className="text-xxs-400 text-tertiary capitalize">
            {voice.age.replace('_', ' ')}
          </span>
        </div>
      </div>
    </div>
  )
}
